import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id]
 * @property {string} [title]
 * @property {boolean} [available]
 * @property {number} [price]
 * @property {number} [compare_at_price]
 * @property {Object} [featured_media]
 * @property {Object} [featured_media.preview_image]
 * @property {string} [featured_media.preview_image.src]
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * How long to wait for a cart:update / cart:error after the puppet click before assuming the add
 * failed. Long enough to cover a slow /cart/add.js plus the bundle picker's variant resolution,
 * short enough that a genuinely stuck request doesn't leave the button looking inert forever.
 */
const CART_RESULT_TIMEOUT_MS = 10000;

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar
 * @property {HTMLButtonElement} addToCartButton
 * @property {HTMLElement} [addToCartLabel]
 * @property {HTMLElement} quantityDisplay
 * @property {HTMLElement} quantityNumber
 * @property {HTMLImageElement} [productImage]
 */

/**
 * Sticky add-to-cart bar. Size/Colour sheets paint locally and drive the main
 * variant-picker radios — this element is never remorphed on variant change.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {ResizeObserver | null} */
  #barResizeObserver = null;

  /** @type {MutationObserver | null} */
  #bundlePriceObserver = null;

  /** @type {number | undefined} */
  #bundlePricePollId;

  /** @type {MutationObserver | null} */
  #bundlePickerPriceObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** True from a sticky Add to Cart tap until its cart result arrives - see #setAdding. */
  #isAdding = false;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /**
   * Size sheet `dialog-component` to reopen after Size Guide closes.
   * @type {HTMLElement | null}
   */
  #reopenSheetAfterSizeGuide = null;

  /** @type {boolean} */
  #sizeGuideHandoffInFlight = false;

  /** @type {MutationObserver | null} */
  #sizeGuideObserver = null;

  connectedCallback() {
    super.connectedCallback();

    // Resolve before #setupIntersectionObserver so the bundle-picker's own button (when this
    // product has one) is already the puppet-click target even if that setup bails out early
    // (e.g. no matching .buy-buttons-block) - see #getBundlePickerAddToCartButton.
    this.#targetAddToCartButton = this.#getBundlePickerAddToCartButton();

    this.#setupIntersectionObserver();
    this.#setupBarHeightTracking();
    this.#setupBundlePriceMirror();
    this.#setupBundlePickerPriceMirror();
    this.#setupMainPickerSync();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    // Sections that morph themselves (featured-product-information) don't emit variantUpdate,
    // and this bar is rendered outside the morph target, so variant-picker aims this one straight
    // at us instead. Same detail shape, so the same handler applies.
    this.addEventListener(ThemeEvents.stickyVariantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    // Capture so we beat the Size Guide trigger's own touchstart/click (those open the drawer
    // while the size sheet is still a modal <dialog>, which freezes the page).
    this.addEventListener('click', this.#handleSizeGuidePointer, { capture: true, signal });
    this.addEventListener('touchstart', this.#handleSizeGuidePointer, {
      capture: true,
      signal,
      passive: false,
    });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#barResizeObserver?.disconnect();
    this.#bundlePriceObserver?.disconnect();
    if (this.#bundlePricePollId) clearInterval(this.#bundlePricePollId);
    this.#bundlePickerPriceObserver?.disconnect();
    this.#sizeGuideObserver?.disconnect();
    this.#reopenSheetAfterSizeGuide = null;
    this.#abortController.abort();
    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
  }

  #getKolioSelectedDiscount() {
    const host = document.querySelector('kolio-bundles');
    return host?.shadowRoot?.querySelector('.kolio-bundles__discount--selected') ?? null;
  }

  #mirrorBundlePrice = () => {
    const selected = this.#getKolioSelectedDiscount();
    if (!selected) return;

    const priceText = this.#stripTrailingZeros(
      selected.querySelector('.kolio-bundles__discount__price .money')?.textContent?.trim() ?? ''
    );
    if (!priceText) return;
    const comparePriceText = this.#stripTrailingZeros(
      selected.querySelector('.kolio-bundles__discount__compare-price .money')?.textContent?.trim() ?? ''
    );
    const discountAmount =
      selected.dataset.discounttype === 'percentage' ? selected.dataset.discountamount : undefined;

    let html = `<span class="price">${priceText}</span>`;
    if (comparePriceText) {
      html += `<span class="compare-at-price">${comparePriceText}</span>`;
      if (discountAmount) {
        html += `<span class="price-badge-break" aria-hidden="true"></span><span class="price-badge">${discountAmount}% off</span>`;
      }
    }

    this.#applyComboPriceHtml(html);
  };

  /**
   * Writes combo/bundle price markup into both places the sticky bar shows a price (the pinned
   * bar itself and the Size/Colour sheet footers), tagging each with its `--combo` modifier class
   * so the CSS that normally hides compare-at-price/badge on the plain sticky price (see
   * sections/product-information.liquid) shows them here instead. Shared by every bundle/combo
   * price source (Kolio today, the bundle-picker block below, whatever comes next) so they can't
   * drift out of sync with each other.
   * @param {string} html
   */
  #applyComboPriceHtml(html) {
    /** @type {[string, NodeListOf<Element>][]} */
    const targetGroups = [
      ['sticky-add-to-cart__price--combo', this.querySelectorAll('.sticky-add-to-cart__price')],
      ['sticky-option-sheet__footer-price--combo', this.querySelectorAll('.sticky-option-sheet__footer-price')],
    ];
    for (const [comboClass, nodes] of targetGroups) {
      for (const node of nodes) {
        node.innerHTML = html;
        node.classList.add(comboClass);
      }
    }
  }

  /**
   * Finds the currently-selected tier of the in-house "Bundle Picker Block" (blocks/bundle-
   * picker.liquid + blocks/bundle-option.liquid - `.bpb-`/`.bpo-` prefixed, the in-progress
   * replacement for the Kolio bundle widget). Scoped to this sticky bar's own product where the
   * block exposes one (data-product-id), falling back to the first bundle-picker block on the
   * page - matches the graceful-degradation style used by #getProductForm/#getVariantPicker.
   * @returns {HTMLElement | null}
   */
  #getSelectedBundleOption() {
    const productId = this.dataset.productId;
    const scoped = productId
      ? document.querySelector(`.bundle-picker-block[data-product-id="${productId}"] .bpo-option.is-selected`)
      : null;
    return /** @type {HTMLElement | null} */ (scoped ?? document.querySelector('.bundle-picker-block .bpo-option.is-selected'));
  }

  /**
   * Finds the bundle-picker block's own Add to Cart button (.bpb-atc-btn) for this sticky bar's
   * product, scoped the same way #getSelectedBundleOption is. Used as the puppet-click target in
   * handleAddToCartClick in place of the plain product form's [ref="addToCartButton"] - a product
   * whose "buy-buttons" block has had its Add to Cart child removed in favor of bundle-picker (as
   * happens once a product is migrated to it) has no such button at all, so the plain-button
   * lookup finds nothing and the sticky bar's own button silently does nothing on click. Even
   * where both still exist, the bundle-picker's button is the only one that knows the selected
   * tier's quantity/discount - the same thing this component's price mirror is already showing -
   * so it must be the one that gets clicked, not a plain "add 1 at full price" button.
   * @returns {HTMLButtonElement | null}
   */
  #getBundlePickerAddToCartButton() {
    const productId = this.dataset.productId;
    const scoped = productId
      ? document.querySelector(`.bundle-picker-block[data-product-id="${productId}"] .bpb-atc-btn`)
      : null;
    return /** @type {HTMLButtonElement | null} */ (scoped ?? document.querySelector('.bundle-picker-block .bpb-atc-btn'));
  }

  /**
   * Mirrors the bundle-picker block's selected tier price into the sticky bar - same job as
   * #mirrorBundlePrice does for Kolio, run alongside it (not instead of it) during the migration.
   *
   * Reads the tier's canonical numeric data (data-bundle-total/-compare-total, cents) rather than
   * scraping the block's own pre-formatted price text, then formats it with this component's own
   * #formatMoney so the sticky bar's price markup/classes stay entirely our own - not dependent on
   * whatever HTML structure the bundle-picker block happens to render into its own sticky bar.
   */
  #mirrorBundlePickerPrice = () => {
    const selected = this.#getSelectedBundleOption();
    if (!selected) return;

    const bundleTotal = parseInt(selected.dataset.bundleTotal ?? '', 10);
    if (!Number.isFinite(bundleTotal)) return;

    // Combo cards strike through Shopify price × qty (data-orig-total), not catalogue MRP
    // (data-compare-total). Using MRP here is what turned a "Buy 3" tier into "36% off"
    // (₹1,797) while the card showed ₹1,647. Match the card: bundle discount vs orig, and
    // only fall back to MRP when the tier has no bundle discount (Buy 1 at the variant price).
    const origTotal = parseInt(selected.dataset.origTotal ?? '', 10);
    const compareTotal = parseInt(selected.dataset.compareTotal ?? '', 10);
    const discType = selected.dataset.discountType;
    const discVal = parseFloat(selected.dataset.discountValue);

    let html = `<span class="price">${this.#formatMoney(bundleTotal)}</span>`;
    const hasBundleDiscount = Number.isFinite(origTotal) && origTotal > bundleTotal;
    const strikeTotal = hasBundleDiscount
      ? origTotal
      : Number.isFinite(compareTotal) && compareTotal > bundleTotal
        ? compareTotal
        : NaN;

    if (Number.isFinite(strikeTotal) && strikeTotal > bundleTotal) {
      const discountPercentage =
        hasBundleDiscount && discType === 'percentage' && discVal > 0
          ? Math.round(discVal)
          : Math.round(((strikeTotal - bundleTotal) * 100) / strikeTotal);
      html += `<span class="compare-at-price">${this.#formatMoney(strikeTotal)}</span>`;
      html += `<span class="price-badge-break" aria-hidden="true"></span><span class="price-badge">${discountPercentage}% off</span>`;
    }

    this.#applyComboPriceHtml(html);
  };

  #setupBundlePriceMirror() {
    if (!this.querySelector('.sticky-add-to-cart__price, .sticky-option-sheet__footer-price')) return;

    const attach = (container) => {
      this.#mirrorBundlePrice();
      this.#bundlePriceObserver = new MutationObserver(this.#mirrorBundlePrice);
      this.#bundlePriceObserver.observe(container, {
        attributes: true,
        attributeFilter: ['class'],
        subtree: true,
      });
    };

    const discountsContainer = this.#getKolioSelectedDiscount()?.parentElement;
    if (discountsContainer) {
      attach(discountsContainer);
      return;
    }

    let attempts = 0;
    this.#bundlePricePollId = setInterval(() => {
      attempts += 1;
      const container = this.#getKolioSelectedDiscount()?.parentElement;
      if (container) {
        clearInterval(this.#bundlePricePollId);
        attach(container);
      } else if (attempts >= 20) {
        clearInterval(this.#bundlePricePollId);
      }
    }, 300);
  }

  /**
   * Same job as #setupBundlePriceMirror, for the bundle-picker block. No polling needed here -
   * unlike Kolio (a third-party app embed that upgrades asynchronously), the bundle-picker
   * block's markup and its data-bundle-total/-compare-total/-per-unit attributes are rendered
   * server-side and present in the initial HTML, so there's no load race to wait out.
   *
   * Watches both `class` (which tier is `.is-selected` - changes when the shopper taps a
   * different tier) and the three price data attributes (which change on the *same* selected
   * tier when #recalcOptionPrices in the block's own script recomputes prices after a size/
   * colour/variant change) - either alone can change the price shown here.
   */
  #setupBundlePickerPriceMirror() {
    if (!this.querySelector('.sticky-add-to-cart__price, .sticky-option-sheet__footer-price')) return;

    const block = this.#getSelectedBundleOption()?.closest('.bundle-picker-block');
    if (!block) return;

    this.#mirrorBundlePickerPrice();
    this.#bundlePickerPriceObserver = new MutationObserver(this.#mirrorBundlePickerPrice);
    this.#bundlePickerPriceObserver.observe(block, {
      attributes: true,
      attributeFilter: ['class', 'data-bundle-total', 'data-compare-total', 'data-per-unit'],
      subtree: true,
    });
  }

  #setupBarHeightTracking() {
    const { stickyBar } = this.refs;
    if (!stickyBar || typeof ResizeObserver === 'undefined') return;

    this.#barResizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
      document.documentElement.style.setProperty('--sticky-add-to-cart-height', `${Math.round(height)}px`);
    });
    this.#barResizeObserver.observe(stickyBar);
  }

  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (!entry.isIntersecting && !this.#isStuck) {
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) this.#showStickyBar();
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          const rect = buyButtonsBlock.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      { rootMargin: '200px 0px 0px 0px' }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    // Bundle-picker button (if this product has one) always wins - already resolved in
    // connectedCallback - since only it knows the selected tier's quantity/discount, which is
    // what the sticky bar's price is showing. Only fall back to the plain product form's button
    // when there's no bundle-picker for this product.
    this.#targetAddToCartButton ??= productForm.querySelector('[ref="addToCartButton"]');
  }

  /**
   * Resolves once the add-to-cart kicked off by the puppet click reports back — `true` only if it
   * actually succeeded. Both the plain product form (assets/product-form.js) and the bundle
   * picker publish these events, so this covers every path the puppet click can take. Falls back
   * to `false` if nothing answers, so a silent failure never gets celebrated.
   * @returns {Promise<boolean>}
   */
  #waitForCartResult() {
    const { signal: teardownSignal } = this.#abortController;
    if (teardownSignal.aborted) return Promise.resolve(false);

    return new Promise((resolve) => {
      const controller = new AbortController();
      /** @type {number} */
      let timeoutId;

      /** @param {boolean} succeeded */
      const settle = (succeeded) => {
        clearTimeout(timeoutId);
        controller.abort();
        resolve(succeeded);
      };

      timeoutId = setTimeout(() => settle(false), CART_RESULT_TIMEOUT_MS);
      const options = { signal: controller.signal, once: true };

      document.addEventListener(ThemeEvents.cartUpdate, () => settle(true), options);
      document.addEventListener(ThemeEvents.cartError, () => settle(false), options);
      teardownSignal.addEventListener('abort', () => settle(false), options);
    });
  }

  handleAddToCartClick = async () => {
    // A repeat tap while the first add is still resolving would queue a second add.
    if (this.#isAdding) return;

    // Re-resolve at click time: on page templates the sticky bar is rendered above
    // the bundle picker, and a connect-time lookup can miss .bpb-atc-btn. That
    // button is the one that auto-applies the selected tier's discount_code.
    this.#targetAddToCartButton =
      this.#getBundlePickerAddToCartButton() || this.#targetAddToCartButton;

    const targetButton = this.#targetAddToCartButton;
    // A disabled target swallows the synthetic click entirely, so there is nothing to wait for.
    if (!targetButton || targetButton.disabled) return;

    targetButton.dataset.puppet = 'true';

    // Listen before clicking so a fast response can't land before we're watching.
    const cartResult = this.#waitForCartResult();

    // The puppet-clicked button's own spinner is usually scrolled off-screen, so without this the
    // sticky bar gave no sign anything was happening while the add resolved.
    this.#setAdding(true);
    targetButton.click();

    // The "added" state and the fly-to-cart animation used to run the moment the click was
    // dispatched, which meant a rejected or blocked add still looked like a success.
    const added = await cartResult;
    this.#setAdding(false);
    if (!added) return;

    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Shows "Adding..." on the bar's and the sheets' Add to Cart buttons while an add is in flight.
   * Deliberately not `disabled`: that attribute already means "sold out" here (#paintAvailability,
   * #syncButtonsFrom), and toggling it back off afterwards could re-enable a sold-out button.
   * Repeat taps are blocked by #isAdding instead.
   * @param {boolean} on
   */
  #setAdding(on) {
    this.#isAdding = on;
    const buttons = [this.refs.addToCartButton, ...this.#footerAddToCartButtons()].filter(Boolean);
    for (const button of buttons) {
      button.toggleAttribute('aria-busy', on);
      button.classList.toggle('is-adding', on);
    }

    const labels = [this.refs.addToCartLabel, ...this.#footerAddToCartLabels()].filter(Boolean);
    const addingText = this.refs.addToCartButton?.dataset.addingText;
    for (const label of labels) {
      if (on) {
        if (!addingText) continue;
        label.dataset.labelBeforeAdding = label.textContent ?? '';
        label.textContent = addingText;
      } else if (label.dataset.labelBeforeAdding !== undefined) {
        label.textContent = label.dataset.labelBeforeAdding;
        delete label.dataset.labelBeforeAdding;
      }
    }
  }

  handleFooterAddToCartClick = async (event) => {
    const button = event.target instanceof Element ? event.target.closest('button') : null;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const dialogComponent = button.closest('dialog-component');
    if (dialogComponent && typeof (/** @type {any} */ (dialogComponent).closeDialog) === 'function') {
      await (/** @type {any} */ (dialogComponent).closeDialog());
    }

    await this.handleAddToCartClick();
  };

  /**
   * Size Guide is a regular fixed overlay (z-index). A modal <dialog> lives in the browser
   * top layer and marks the rest of the page inert — CSS cannot put Size Guide above it, and
   * hiding the open dialog with display:none still leaves the page inert. Close the sheet
   * first, then open Size Guide; reopen the sheet when Size Guide closes.
   * Main-picker Size Guide is unchanged: this only runs when the trigger is inside an open sheet.
   * @param {Event} event
   */
  #handleSizeGuidePointer = async (event) => {
    const trigger =
      event.target instanceof Element ? event.target.closest('.size-guide-link[data-drawer-trigger]') : null;
    if (!(trigger instanceof HTMLElement)) return;

    const dialogComponent = trigger.closest('dialog-component');
    if (!dialogComponent) return;

    const dialog = dialogComponent.querySelector('dialog');
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (this.#sizeGuideHandoffInFlight) return;
    this.#sizeGuideHandoffInFlight = true;
    this.#reopenSheetAfterSizeGuide = dialogComponent;

    try {
      if (typeof (/** @type {any} */ (dialogComponent).closeDialog) === 'function') {
        await (/** @type {any} */ (dialogComponent).closeDialog());
      }
      this.#openSizeGuideFromTrigger(trigger);
    } finally {
      this.#sizeGuideHandoffInFlight = false;
    }
  };

  /** @param {HTMLElement} trigger */
  #openSizeGuideFromTrigger(trigger) {
    const sectionId = trigger.getAttribute('data-drawer-trigger');
    if (!sectionId) return;

    const drawerId = `size-guide-drawer-${sectionId}`;
    const candidates = document.querySelectorAll(`[id="${CSS.escape(drawerId)}"]`);
    /** @type {HTMLElement | null} */
    let drawer = null;
    for (const candidate of candidates) {
      if (candidate instanceof HTMLElement && candidate.parentElement === document.body) {
        drawer = candidate;
        break;
      }
    }
    if (!drawer) {
      const first = candidates[0];
      drawer = first instanceof HTMLElement ? first : null;
    }
    if (!drawer) {
      const sheet = this.#reopenSheetAfterSizeGuide;
      this.#reopenSheetAfterSizeGuide = null;
      if (sheet && typeof (/** @type {any} */ (sheet).showDialog) === 'function') {
        (/** @type {any} */ (sheet).showDialog)();
      }
      return;
    }

    this.#observeSizeGuide(drawer);

    const openDrawer = /** @type {{ _openDrawer?: Function }} */ (drawer)._openDrawer;
    if (typeof openDrawer === 'function') {
      openDrawer(null, trigger);
      return;
    }

    if (drawer.parentElement !== document.body) document.body.appendChild(drawer);
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('drawer-open');
  }

  /** @param {HTMLElement} drawer */
  #observeSizeGuide(drawer) {
    if (this.#sizeGuideObserver) return;

    this.#sizeGuideObserver = new MutationObserver(() => {
      if (drawer.getAttribute('aria-hidden') !== 'true') return;

      const sheet = this.#reopenSheetAfterSizeGuide;
      this.#reopenSheetAfterSizeGuide = null;
      if (!sheet || typeof (/** @type {any} */ (sheet).showDialog) !== 'function') return;

      (/** @type {any} */ (sheet).showDialog)();
    });
    this.#sizeGuideObserver.observe(drawer, { attributes: true, attributeFilter: ['aria-hidden'] });
  }

  handleSheetValueClick = async (event) => {
    const button = event.target;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const sheet = button.closest('[data-sticky-option-sheet]');
    const optionName = sheet instanceof HTMLElement ? sheet.dataset.optionName : undefined;
    const value = button.dataset.stickySheetValue;
    if (!optionName || !value) return;

    const variantPicker = this.#getVariantPicker();
    if (!variantPicker) return;

    const targetInput = this.#findPickerInput(variantPicker, optionName, value);
    if (!targetInput || targetInput.checked) return;

    this.#paintSelection(optionName, value, button.querySelector('.swatch'));
    this.#paintAvailability(button.getAttribute('aria-disabled') !== 'true');

    targetInput.checked = true;
    targetInput.dispatchEvent(new Event('change', { bubbles: true }));
  };

  /**
   * @param {string} optionName
   * @param {string} value
   * @param {Element | null} [sourceSwatchEl]
   */
  #paintSelection(optionName, value, sourceSwatchEl = null) {
    const sheet = this.querySelector(`[data-sticky-option-sheet][data-option-name="${CSS.escape(optionName)}"]`);
    if (!sheet) return;

    for (const sibling of sheet.querySelectorAll('[data-sticky-sheet-value]')) {
      const isSelected = sibling.dataset.stickySheetValue === value;
      sibling.classList.toggle('is-selected', isSelected);
      sibling.setAttribute('aria-pressed', String(isSelected));
    }

    // Pack-of-product's Colour sheet shows the selected name once in its section header
    // (see snippets/sticky-add-to-cart-bar.liquid) instead of under every swatch photo -- keep
    // it in sync here so both the tap-to-select path (this method, called directly from
    // handleSheetValueClick) and the AJAX variant-switch resync path (#syncSheetOptionsFrom,
    // which also calls this method) update it the same way. A no-op for every other product,
    // since their sheets never render this element.
    const headerValue = sheet.querySelector('[data-sticky-sheet-selected-label]');
    if (headerValue) headerValue.textContent = value;

    const gridItem = sheet.closest('.sticky-add-to-cart__variant-option-grid-item');
    const trigger = gridItem?.querySelector('.sticky-add-to-cart__option-trigger');
    const label = trigger?.querySelector('.sticky-add-to-cart__option-select');
    if (label) label.textContent = value;

    const triggerSwatch = trigger?.querySelector('.swatch');
    // With colour swatch images on, the sheet shows photos but the trigger keeps the colour dot:
    // take the selected value's hidden dot from the sheet instead of the photo swatch passed in
    // from the main variant picker (see data-sticky-dot-swatch in sticky-add-to-cart-bar.liquid).
    const dotWrap = sheet
      .querySelector(`[data-sticky-sheet-value="${CSS.escape(value)}"]`)
      ?.querySelector('[data-sticky-dot-swatch]');
    const source = dotWrap ? dotWrap.querySelector('.swatch') : sourceSwatchEl;
    if (source && triggerSwatch) triggerSwatch.outerHTML = source.outerHTML;
  }

  /** @param {boolean} available */
  #paintAvailability(available) {
    const { addToCartButton, addToCartLabel } = this.refs;
    const label = available ? addToCartButton?.dataset.addToCartText : addToCartButton?.dataset.soldOutText;

    if (addToCartButton) addToCartButton.disabled = !available;
    if (addToCartLabel && label) addToCartLabel.textContent = label;

    for (const button of this.#footerAddToCartButtons()) {
      button.disabled = !available;
    }
    if (label) {
      for (const footerLabel of this.#footerAddToCartLabels()) {
        footerLabel.textContent = label;
      }
    }
  }

  /**
   * The Size/Colour sheet footers live inside their own `dialog-component`, which is itself a
   * `Component` and so claims their `ref="footerAddToCartButtons[]"` / `ref="footerAddToCartLabels[]"`
   * elements as its own refs before they ever reach `this.refs` (refs are scoped to the nearest
   * Component ancestor). Querying the DOM directly sidesteps that boundary.
   * @returns {HTMLButtonElement[]}
   */
  #footerAddToCartButtons() {
    return Array.from(this.querySelectorAll('[ref="footerAddToCartButtons[]"]'));
  }

  /** @returns {HTMLElement[]} */
  #footerAddToCartLabels() {
    return Array.from(this.querySelectorAll('[ref="footerAddToCartLabels[]"]'));
  }

  #setupMainPickerSync() {
    const variantPicker = this.#getVariantPicker();
    if (!variantPicker) return;

    const sheets = Array.from(this.querySelectorAll('[data-sticky-option-sheet]'));
    if (!sheets.length) return;

    const { signal } = this.#abortController;
    variantPicker.addEventListener(
      'change',
      (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

        const sheet = sheets.find((s) => input.name.startsWith(`${s.getAttribute('data-option-name')}-`));
        if (!sheet) return;

        const sourceSwatch = input.closest('label')?.querySelector('.swatch') ?? null;
        this.#paintSelection(sheet.getAttribute('data-option-name'), input.value, sourceSwatch);
        this.#paintAvailability(input.dataset.optionAvailable !== 'false');
      },
      { signal }
    );
  }

  /**
   * Copy price, sold-out state, and option availability from the section HTML.
   * Does not morph/replace the sticky bar, so the open sheet and bar layout stay put.
   * @param {CustomEvent} event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;
    const newSticky = event.detail.data.html?.querySelector('sticky-add-to-cart');

    this.dataset.variantAvailable = newSticky?.dataset.variantAvailable ?? String(Boolean(variant?.available));

    if (variant?.id) {
      this.dataset.currentVariantId = variant.id;
      if (variant.price != null) this.dataset.unitPrice = String(variant.price);
      if (variant.compare_at_price != null) this.dataset.unitComparePrice = String(variant.compare_at_price);

      const variantIdInput = this.querySelector('[data-variant-id-input]');
      if (variantIdInput instanceof HTMLInputElement) variantIdInput.value = String(variant.id);

      const quantityInput = this.querySelector('[data-quantity-input]');
      if (quantityInput instanceof HTMLInputElement) quantityInput.value = String(this.#currentQuantity);
    } else {
      this.dataset.currentVariantId = '';
    }

    if (newSticky) {
      this.#copyInnerHTML(newSticky, '.sticky-add-to-cart__price, .sticky-option-sheet__footer-price');
      this.#syncButtonsFrom(newSticky);
      this.#syncSheetOptionsFrom(newSticky);
    } else if (variant == null || variant.available === false) {
      this.#paintAvailability(false);
    }

    this.#updateImages(variant);
    this.#updateButtonText();
    this.#updatePriceForQuantity();
    this.#mirrorBundlePrice();
    this.#mirrorBundlePickerPrice();
  };

  /** @param {Element} source */
  #copyInnerHTML(source, selector) {
    const next = source.querySelectorAll(selector);
    const current = this.querySelectorAll(selector);
    next.forEach((node, i) => {
      if (!current[i]) return;
      current[i].innerHTML = node.innerHTML;
      current[i].classList.remove('sticky-add-to-cart__price--combo', 'sticky-option-sheet__footer-price--combo');
    });
  }

  /** @param {Element} source */
  #syncButtonsFrom(source) {
    const newBtn = source.querySelector('[ref="addToCartButton"]');
    if (newBtn instanceof HTMLButtonElement && this.refs.addToCartButton) {
      this.refs.addToCartButton.disabled = newBtn.disabled;
    }

    const newLabel = source.querySelector('[ref="addToCartLabel"]');
    if (newLabel && this.refs.addToCartLabel) {
      this.refs.addToCartLabel.textContent = newLabel.textContent;
    }

    const newFooters = source.querySelectorAll('[ref="footerAddToCartButtons[]"]');
    this.#footerAddToCartButtons().forEach((button, i) => {
      const next = newFooters[i];
      if (next instanceof HTMLButtonElement) button.disabled = next.disabled;
    });

    const newFooterLabels = source.querySelectorAll('[ref="footerAddToCartLabels[]"]');
    this.#footerAddToCartLabels().forEach((label, i) => {
      if (newFooterLabels[i]) label.textContent = newFooterLabels[i].textContent;
    });
  }

  /** @param {Element} source */
  #syncSheetOptionsFrom(source) {
    for (const newSheet of source.querySelectorAll('[data-sticky-option-sheet]')) {
      if (!(newSheet instanceof HTMLElement)) continue;
      const name = newSheet.dataset.optionName;
      if (!name) continue;

      const sheet = this.querySelector(`[data-sticky-option-sheet][data-option-name="${CSS.escape(name)}"]`);
      if (!sheet) continue;

      for (const newBtn of newSheet.querySelectorAll('[data-sticky-sheet-value]')) {
        if (!(newBtn instanceof HTMLElement) || !newBtn.dataset.stickySheetValue) continue;
        const btn = sheet.querySelector(
          `[data-sticky-sheet-value="${CSS.escape(newBtn.dataset.stickySheetValue)}"]`
        );
        if (!(btn instanceof HTMLElement)) continue;

        const selected = newBtn.classList.contains('is-selected');
        btn.classList.toggle('is-selected', selected);
        btn.setAttribute('aria-pressed', String(selected));
        if (newBtn.hasAttribute('aria-disabled')) btn.setAttribute('aria-disabled', 'true');
        else btn.removeAttribute('aria-disabled');
      }

      const selectedBtn = sheet.querySelector('[data-sticky-sheet-value].is-selected');
      if (selectedBtn instanceof HTMLElement && selectedBtn.dataset.stickySheetValue) {
        this.#paintSelection(name, selectedBtn.dataset.stickySheetValue, selectedBtn.querySelector('.swatch'));
      }
    }
  }

  /** @param {ProductVariant | null | undefined} variant */
  #updateImages(variant) {
    const src = variant?.featured_media?.preview_image?.src;
    if (!src) return;
    const sized = src.includes('?') ? `${src}&width=120` : `${src}?width=120`;
    for (const img of this.querySelectorAll('.sticky-add-to-cart__image-img, .sticky-option-sheet__thumb')) {
      if (img instanceof HTMLImageElement) img.src = sized;
    }
  }

  /** @param {CustomEvent} event */
  #handleVariantSelected = (event) => {
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /** @param {CustomEvent} _event */
  #handleCartAddComplete = (_event) => {
    if (this.#targetAddToCartButton) this.#targetAddToCartButton.dataset.puppet = 'false';
  };

  /** @param {QuantitySelectorUpdateEvent} event */
  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
    this.#updatePriceForQuantity();

    const quantityInput = this.querySelector('[data-quantity-input]');
    if (quantityInput instanceof HTMLInputElement) quantityInput.value = String(this.#currentQuantity);
  };

  #showStickyBar() {
    this.#isStuck = true;
    this.refs.stickyBar.dataset.stuck = 'true';
    // Plain class toggle, not a live `body:has([data-stuck='true'])` selector - :has() forces the
    // browser to re-check its condition against the DOM on broad invalidations (e.g. any dialog's
    // scroll-lock touching body's own style), and this page alone carries 260+ :has() rules across
    // its stylesheets. A class match is O(1); see product-add-on.liquid's use of this class.
    document.body.classList.add('sticky-add-to-cart-stuck');
  }

  #hideStickyBar() {
    this.#isStuck = false;
    this.refs.stickyBar.dataset.stuck = 'false';
    document.body.classList.remove('sticky-add-to-cart-stuck');
  }

  /** @returns {HTMLElement | null} */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /** @returns {Element | null} */
  #getVariantPicker() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(`#shopify-section-${sectionId} variant-picker[data-product-id="${productId}"]`);
  }

  /**
   * @param {Element} variantPicker
   * @param {string} optionName
   * @param {string} value
   * @returns {HTMLInputElement | undefined}
   */
  #findPickerInput(variantPicker, optionName, value) {
    const namePrefix = `${optionName}-`;
    return /** @type {HTMLInputElement | undefined} */ (
      Array.from(variantPicker.querySelectorAll('input[type="radio"]')).find(
        (input) => input instanceof HTMLInputElement && input.name.startsWith(namePrefix) && input.value === value
      )
    );
  }

  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
    this.#updatePriceForQuantity();
  }

  /** @param {string} text */
  #stripTrailingZeros(text) {
    return text.replace(/\.00\b/g, '');
  }

  /** @param {number} amountCents */
  #formatMoney(amountCents) {
    const format = this.dataset.moneyFormat || '₹{{amount}}';
    const value = (amountCents / 100).toLocaleString('en-IN', {
      minimumFractionDigits: amountCents % 100 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    });
    return this.#stripTrailingZeros(format.replace(/\{\{\s*amount\s*\}\}/g, value));
  }

  #updatePriceForQuantity() {
    const unitPrice = parseInt(this.dataset.unitPrice || '', 10);
    if (!unitPrice) return;

    const priceNodes = this.querySelectorAll('.sticky-add-to-cart__price, .sticky-option-sheet__footer-price');
    for (const node of priceNodes) {
      if (node.classList.contains('sticky-add-to-cart__price--combo')) continue;
      if (node.classList.contains('sticky-option-sheet__footer-price--combo')) continue;

      const priceEl = node.querySelector('.price');
      if (priceEl) priceEl.innerHTML = this.#formatMoney(unitPrice * this.#currentQuantity);
    }
  }

  #updateButtonText() {
    const available = !this.refs.addToCartButton?.disabled;
    const showQty = Boolean(available && this.#currentQuantity > 1);
    const qty = String(this.#currentQuantity);

    for (const el of this.querySelectorAll('.quantity-number')) {
      el.textContent = qty;
    }
    for (const el of this.querySelectorAll('.quantity-display')) {
      el.style.display = showQty ? 'inline' : 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
